/**
 * WizardStepIndicator Component
 *
 * Shows the current step in the email wizard flow.
 * Steps: 1. Template → 2. Recipients → 3. Send Mode → 4. Review & Send
 */

import React from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';

interface WizardStepIndicatorProps {
  currentStep: 1 | 2 | 3 | 4;
  onStepClick?: (step: 1 | 2 | 3 | 4) => void;
  /** Callback for previous step navigation */
  onPrevious?: () => void;
  /** Callback for next step navigation */
  onNext?: () => void;
  /** Whether next button should be disabled (e.g., no selection made) */
  canGoNext?: boolean;
}

const STEPS = [
  { number: 1, label: 'Template' },
  { number: 2, label: 'Recipients' },
  { number: 3, label: 'Send Mode' },
  { number: 4, label: 'Review' },
] as const;

export const WizardStepIndicator: React.FC<WizardStepIndicatorProps> = ({
  currentStep,
  onStepClick,
  onPrevious,
  onNext,
  canGoNext = true,
}) => {
  const canGoPrevious = currentStep > 1;
  const canGoForward = currentStep < 4 && canGoNext;

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 px-2 sm:px-4 bg-dark-bg/50 border-b border-dark-border/30">
      {/* Previous Arrow */}
      <button
        onClick={onPrevious}
        disabled={!canGoPrevious || !onPrevious}
        className={`p-1.5 rounded-full transition-all duration-150 ${
          canGoPrevious && onPrevious
            ? 'text-dark-muted hover:text-white hover:bg-dark-border'
            : 'text-dark-muted cursor-not-allowed'
        }`}
        title={canGoPrevious ? 'Previous step' : ''}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {STEPS.map((step, index) => {
        const isCompleted = step.number < currentStep;
        const isCurrent = step.number === currentStep;
        const isClickable = onStepClick && step.number <= currentStep;

        return (
          <React.Fragment key={step.number}>
            {/* Step circle and label */}
            <button
              onClick={() => isClickable && onStepClick?.(step.number as 1 | 2 | 3 | 4)}
              disabled={!isClickable}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all ${
                isClickable ? 'cursor-pointer hover:bg-dark-border' : 'cursor-default'
              } ${
                isCurrent
                  ? 'bg-purple-500/20 border border-purple-500'
                  : isCompleted
                  ? 'bg-green-500/20 border border-green-500/50'
                  : 'bg-dark-border/50 border border-dark-border/30'
              }`}
            >
              {/* Step number or checkmark */}
              <span
                className={`w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center rounded-full text-[10px] sm:text-xs font-bold ${
                  isCurrent
                    ? 'bg-purple-500 text-white'
                    : isCompleted
                    ? 'bg-green-500 text-white'
                    : 'bg-dark-border text-dark-muted'
                }`}
              >
                {isCompleted ? <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : step.number}
              </span>
              {/* Step label - hidden on very small screens */}
              <span
                className={`text-[10px] sm:text-xs font-medium hidden xs:inline ${
                  isCurrent
                    ? 'text-purple-400'
                    : isCompleted
                    ? 'text-green-400'
                    : 'text-dark-muted'
                }`}
              >
                {step.label}
              </span>
            </button>

            {/* Connector line (except after last step) */}
            {index < STEPS.length - 1 && (
              <div
                className={`w-4 sm:w-8 h-0.5 ${
                  step.number < currentStep ? 'bg-green-500' : 'bg-dark-border'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Next Arrow */}
      <button
        onClick={onNext}
        disabled={!canGoForward || !onNext}
        className={`p-1.5 rounded-full transition-all duration-150 ${
          canGoForward && onNext
            ? 'text-dark-muted hover:text-white hover:bg-dark-border'
            : 'text-dark-muted cursor-not-allowed'
        }`}
        title={canGoForward ? 'Next step' : ''}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default WizardStepIndicator;
